import pathOr from "@ramda/pathor";
import { FormikHelpers } from "formik";
import { TFunction } from "i18next";
import { List, Map } from "immutable";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import * as yup from "yup";
import {
  closeRightPanel,
  openRightPanel,
} from "../../../../reduxFolder/reducers/rightPanel";
import { updateExpectedItem } from "../../../../reduxFolder/reducers/storage";
import { requestsSelectServiceTypes } from "../../../../reduxFolder/selectors/serviceRequestsSelectors";
import { ExpectedShipmentServiceRequestDto } from "../../../../types/api/expected";
import { toastResponseError } from "../../../../utils/responseMessageHelper";
import { getRequestInfoForType } from "../../../Storage/components/ServiceRequests/requestHelper";

interface ExpectedServiceRequestFormValues {
  comment: string;
  selectedGroupOption?: string | string[];
}

export interface ExpectedServiceRequestProps {
  panelData: any;
  openRightPanel: OpenRightPanel;
  closeRightPanel?: any;
  updateExpectedItem?: any;
  serviceRequestsTypes: any;
  isLoading: boolean;
  [key: string]: any;
}

export const getActiveServiceRequests = (
  panelData: any,
): ExpectedShipmentServiceRequestDto[] => {
  const raw = panelData.get("service_requests");
  if (!raw) return [];
  const list = raw.toJS ? raw.toJS() : raw;
  return list.map((request: any) => ({
    service_request_type_id: request.service_request_type_id,
    user_comment: request.user_comment,
  }));
};

export const getActiveTypeIds = (panelData: any): number[] => {
  return getActiveServiceRequests(panelData).map(
    (request) => request.service_request_type_id,
  );
};

export const isServiceTypeActive = (
  panelData: any,
  serviceRequestsTypes: any,
  requestType: string,
): boolean => {
  const dynamicRequestType = getRequestInfoForType(
    serviceRequestsTypes,
    requestType,
  );
  if (!dynamicRequestType) return false;

  const isDynamicComplexRequest = !!dynamicRequestType.get("complexType");
  if (isDynamicComplexRequest) {
    const requestKeys = dynamicRequestType.get("requestKeys");
    if (!requestKeys) return false;
    const subTypeId = requestKeys.get(requestType)?.get("id");
    return subTypeId != null && getActiveTypeIds(panelData).includes(subTypeId);
  }

  const typeId = dynamicRequestType.get("id");
  return typeId != null && getActiveTypeIds(panelData).includes(typeId);
};

export const getActiveExpectedRequest = (
  panelData: any,
  serviceRequestsTypes: any,
  requestType: string,
) => {
  const dynamicRequestType = getRequestInfoForType(
    serviceRequestsTypes,
    requestType,
  );
  if (!dynamicRequestType) return undefined;

  const typeId = dynamicRequestType.get("complexType")
    ? dynamicRequestType.get("requestKeys")?.get(requestType)?.get("id")
    : dynamicRequestType.get("id");
  if (typeId == null) return undefined;

  return panelData
    .get("service_requests", List())
    .find((request: any) => request.get("service_request_type_id") === typeId);
};

export const toggleExpectedServiceRequests = (
  panelData: any,
  serviceRequestsTypes: any,
  requestTypes: string[],
  userComment: string,
): ExpectedShipmentServiceRequestDto[] => {
  const requestsByTypeId = new globalThis.Map<
    number,
    ExpectedShipmentServiceRequestDto
  >(
    getActiveServiceRequests(panelData).map((request) => [
      request.service_request_type_id,
      request,
    ]),
  );

  for (const requestType of requestTypes) {
    const dynamicRequestType = getRequestInfoForType(
      serviceRequestsTypes,
      requestType,
    );
    if (!dynamicRequestType) continue;

    const typeId = dynamicRequestType.get("complexType")
      ? dynamicRequestType.get("requestKeys")?.get(requestType)?.get("id")
      : dynamicRequestType.get("id");
    if (typeId == null) continue;

    if (requestsByTypeId.has(typeId)) {
      requestsByTypeId.delete(typeId);
    } else {
      requestsByTypeId.set(typeId, {
        service_request_type_id: typeId,
        user_comment: userComment,
      });
    }
  }

  return Array.from(requestsByTypeId.values());
};

const handleExpectedRequestSubmit = (
  values: ExpectedServiceRequestFormValues,
  {
    setSubmitting,
    props: {
      panelData,
      openRightPanel,
      updateExpectedItem,
      serviceRequestsTypes = List(),
    },
  }: {
    setSubmitting: (isSubmitting: boolean) => void;
    props: ExpectedServiceRequestProps;
  },
) => {
  let requestTypeArray =
    values.selectedGroupOption ?? panelData.get("requestType");
  if (!Array.isArray(requestTypeArray)) {
    requestTypeArray = [requestTypeArray];
  }

  const itemId = panelData.get("id");
  const payload = {
    expected_shipment: {
      service_requests: toggleExpectedServiceRequests(
        panelData,
        serviceRequestsTypes,
        requestTypeArray,
        values.comment,
      ),
    },
  };

  return updateExpectedItem(itemId, payload)
    .then((response: any) => {
      const value = pathOr(Map(), ["value"], response);
      const data = value.get("data");
      if (data) {
        openRightPanel(
          "EXPECTED_ITEM",
          data.merge(
            Map({
              isEditForm: true,
              selectedExpectedItem: data,
            }),
          ),
        );
      }
    })
    .catch((error: Error) => {
      toastResponseError(error);
    })
    .finally(() => {
      setSubmitting(false);
    });
};

export const withExpectedConnect = connect(
  createStructuredSelector({
    serviceRequestsTypes: requestsSelectServiceTypes,
  }),
  {
    openRightPanel,
    closeRightPanel,
    updateExpectedItem,
  },
);

export const expectedServiceRequestFormik = ({
  t,
  ...props
}: ExpectedServiceRequestProps & { t: TFunction }) => ({
  validateOnChange: true,
  validateOnBlur: false,
  initialValues: {
    comment: "",
  },
  validationSchema: () => {
    const comment = yup
      .string()
      .max(255, t("error.stringShorter", { length: 255 }));
    const requestType = props.panelData.get("requestType");
    const dynamicRequestType = getRequestInfoForType(
      props.serviceRequestsTypes,
      requestType,
    );

    if (!dynamicRequestType) return yup.object({ comment });

    const isDynamicComplexRequest = !!dynamicRequestType.get("complexType");
    const hasRequest = isServiceTypeActive(
      props.panelData,
      props.serviceRequestsTypes,
      requestType,
    );

    if (hasRequest || !isDynamicComplexRequest) {
      return yup.object({ comment });
    }

    if (dynamicRequestType.get("complexType") === "group") {
      return yup.object({
        comment,
        selectedGroupOption: yup
          .string()
          .required(t("error.required", { field: t("error.requiredField") })),
      });
    }

    return yup.object({
      comment,
      selectedGroupOption: yup
        .array()
        .required(t("error.required", { field: t("error.requiredField") }))
        .min(1, t("error.required", { field: t("error.requiredField") })),
    });
  },
  onSubmit: (
    values: ExpectedServiceRequestFormValues,
    formikBag: FormikHelpers<ExpectedServiceRequestFormValues>,
  ) => handleExpectedRequestSubmit(values, { props, ...formikBag }),
});
